/* ══════════════════════════════════════════════════════════════════════════
   loading.js — the waits this system actually has.
   A wait is not decoration: each one names the step it is on, so a wait that
   stalls tells you where it stalled instead of spinning forever.
   ══════════════════════════════════════════════════════════════════════════ */
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence } from "framer-motion";
import { html, M, Ico } from "./ui.js";

const EASE = [.16,1,.3,1];

/* The mark drawing itself. Used by every wait so they read as one system. */
export const Sigil = ({ w = 64, tone = "var(--aqua)" }) => html`
  <svg viewBox="0 0 24 24" width=${w} height=${w} fill="none" style=${{ display:"block" }}>
    <${M.path} d="M12 2.6 20.4 6v6.2c0 5-3.5 8.8-8.4 10.4C7.1 21 3.6 17.2 3.6 12.2V6z"
      stroke=${tone} strokeWidth="1.1" strokeLinejoin="round"
      initial=${{ pathLength:0, opacity:0 }} animate=${{ pathLength:1, opacity:1 }}
      transition=${{ duration:1.5, ease:EASE }}/>
    <${M.path} d="M12 8v8M8.4 11.2 12 8l3.6 3.2" stroke=${tone} strokeWidth="1.1"
      strokeLinecap="round" strokeLinejoin="round"
      initial=${{ pathLength:0, opacity:0 }} animate=${{ pathLength:1, opacity:.62 }}
      transition=${{ duration:1, delay:.5, ease:EASE }}/>
  </svg>`;

/* Three rings, each turning at its own rate. Reads as machinery, not a spinner. */
export const Rings = ({ w = 132, tone = "var(--aqua)" }) => html`
  <div className="rings" style=${{ width:w, height:w, "--tone":tone }}>
    <${M.i} animate=${{ rotate:360 }} transition=${{ duration:9,  repeat:Infinity, ease:"linear" }}/>
    <${M.i} animate=${{ rotate:-360 }} transition=${{ duration:14, repeat:Infinity, ease:"linear" }}/>
    <${M.i} animate=${{ rotate:360 }} transition=${{ duration:22, repeat:Infinity, ease:"linear" }}/>
  </div>`;

/* A named wait. steps is [label, ms] — the labels are the real work. */
export function Boot({ title, say, steps, tone = "var(--aqua)", onDone }){
  const [at,setAt] = useState(0);
  const doneRef = useRef(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  useEffect(()=>{
    if(at >= steps.length){
      if(!doneRef.current){
        doneRef.current = true;
        const fn = onDoneRef.current;
        if(fn) Promise.resolve().then(()=>fn());
      }
      return;
    }
    const id = setTimeout(()=>setAt(a=>a+1), steps[at][1]);
    return ()=>clearTimeout(id);
  },[at,steps]);

  return html`
    <${M.div} className="boot" initial=${{ opacity:0 }} animate=${{ opacity:1 }}
        exit=${{ opacity:0 }} transition=${{ duration:.32 }}>
      <div className="boot__aura"><i/><i/></div>
      <div className="boot__in">
        <div className="boot__mark">
          <${Rings} tone=${tone}/>
          <span className="boot__sig"><${Sigil} w=${52} tone=${tone}/></span>
        </div>
        <h2 style=${{ color:"var(--fg)" }}>${title}</h2>
        <p>${say}</p>

        <div className="boot__steps">
          ${steps.map(([label],i)=> html`
            <div key=${label} className="boot__step" data-s=${i<at?"done":i===at?"now":"wait"}>
              <span className="boot__tick">
                ${i<at ? html`<${Ico} d="check" w=${11}/>`
                       : i===at ? html`<${M.i} className="boot__spin"
                            animate=${{ rotate:360 }} transition=${{ duration:.9, repeat:Infinity, ease:"linear" }}/>`
                       : null}
              </span>
              <span className="boot__lbl">${label}</span>
            </div>`)}
        </div>

        <div className="boot__rail">
          <${M.i} initial=${{ width:0 }} animate=${{ width:(at/steps.length*100)+"%" }}
            transition=${{ duration:.5, ease:EASE }} style=${{ background:tone }}/>
        </div>
      </div>
    <//>`;
}

/* Signing in. The steps are the ones the server really performs. */
export const SIGN_IN = [
  ["Checking credentials", 620],
  ["Applying the password policy", 420],
  ["Opening an 8-hour session", 540],
  ["Loading your capabilities", 480],
];

/* Creating an account (signup/start). Soft invent of "already approved" stays deleted —
   approval is a later Live step after verify. */
export const SIGN_UP = [
  ["Reserving the username", 520],
  ["Storing a password hash", 480],
  ["Sending the email code", 640],
  ["Placing the voice call", 700],
];

/* Forgot-password: email code leave. Bound to Live POST; labels are human only. */
export const FORGOT_SEND = [
  ["Checking the account pair", 480],
  ["Issuing a six-digit code", 520],
  ["Handing the email to the mail system", 700],
  ["Confirming delivery path", 420],
];

/* Forgot-password: set new password. */
export const FORGOT_SAVE = [
  ["Checking the verification code", 520],
  ["Writing the new password", 640],
  ["Ending every other session", 480],
  ["Returning you to sign-in", 420],
];

/* Cold start. Nothing here is faked — each line is a real precondition. */
export const COLD = [
  ["Reading the session", 460],
  ["Resolving capabilities", 420],
  ["Fetching site and zones", 520],
  ["Opening the event stream", 600],
];

/* Always painted against the document, never inside whichever card asked for
   it — an auth panel or a glass surface would otherwise clip it. */
export const Overlay = ({ show, ...rest }) => createPortal(html`
  <${AnimatePresence}>${show && html`<${Boot} key="boot" ...${rest}/>`}<//>`, document.body);
